import { CaretRightIcon } from '@phosphor-icons/react';
import classNames from 'classnames';
import { isEqual } from 'es-toolkit';
import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useDebounce } from 'react-use';

import { translate } from 'waldur-i18n-runtime';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  BaseButton,
  RemoveFilterBadgeButton,
  MenuPopover,
  Tag,
} from 'waldur-ui';

import { TableFilterContext } from './FilterContextProvider';
import { closeFlyoutOnTabOut } from './filterMenuFocus';
import { selectFilterValues } from './selectors';

const DELAY_WAITING_FOR_FILTER = 50; // ms

export interface TableFilterItemProps {
  title: string;
  name?: string;
  badgeValue?(value: any): string | number;
  getValueLabel?(value: any): string | number;
  ellipsis?: boolean;
  showValueBadge?: boolean;
  hideRemoveButton?: boolean;
  onApply?({ title, name, value }): void;
  /** Set to `false` to show "Apply" and "Cancel" buttons */
  instantApply?: boolean;
}

export { RemoveFilterBadgeButton };

// Value labels can be elements (badgeValue/getValueLabel may render
// markup), which can't go into aria-label — fall back to a generic name.
const removeValueLabel = (label) =>
  typeof label === 'string' || typeof label === 'number'
    ? translate('Remove {value}', { value: label })
    : translate('Remove filter value');

export const TableSidebarFilterValues = ({
  value,
  getValueLabel,
  badgeValue = null,
  remove,
  hideRemoveButton = false,
}) => {
  return !['', undefined].includes(value) ? (
    badgeValue ? (
      badgeValue(value) ? (
        <div>
          <Tag
            onClear={hideRemoveButton ? undefined : () => remove(value, value)}
            clearLabel={removeValueLabel(badgeValue(value))}
          >
            {badgeValue(value)}
          </Tag>
        </div>
      ) : null
    ) : Array.isArray(value) ? (
      <>
        {value.map((v, i) => (
          <Tag
            key={i}
            onClear={hideRemoveButton ? undefined : () => remove(value, v)}
            clearLabel={removeValueLabel(getValueLabel(v))}
          >
            {getValueLabel(v)}
          </Tag>
        ))}
      </>
    ) : (
      <Tag
        onClear={hideRemoveButton ? undefined : () => remove(value, value)}
        clearLabel={removeValueLabel(getValueLabel(value))}
      >
        {getValueLabel(value)}
      </Tag>
    )
  ) : null;
};

const TableSidebarFilterItem: FC<PropsWithChildren<TableFilterItemProps>> = ({
  getValueLabel = (value) => {
    if (value) {
      if (Array.isArray(value)) {
        return value.length;
      } else {
        return value?.label || value;
      }
    } else return value;
  },
  ...props
}) => {
  const { table, setFilter, changeFilterValue } =
    React.useContext(TableFilterContext);
  const values = useSelector(selectFilterValues(table));

  const _setFilterRef = useRef<any>();

  const removeValue = useCallback(
    (prevValue, value) => {
      let newValue;
      if (Array.isArray(prevValue) && prevValue.length > 1) {
        newValue = prevValue.filter((v) => !isEqual(v, value));
      } else {
        newValue = null;
      }
      if (changeFilterValue) {
        changeFilterValue(props.name, newValue);
      }
      if (_setFilterRef.current) {
        _setFilterRef.current(newValue);
      }
    },
    [changeFilterValue, props.name],
  );

  const _setFilter = useCallback(
    (value) => {
      setFilter({
        label: props.title,
        name: props.name,
        value: value,
        component: () => (
          <TableSidebarFilterValues
            value={value}
            getValueLabel={getValueLabel}
            badgeValue={props.badgeValue}
            remove={removeValue}
            hideRemoveButton={props.hideRemoveButton}
          />
        ),
      });
    },
    [
      props.title,
      props.name,
      getValueLabel,
      props.badgeValue,
      removeValue,
      props.hideRemoveButton,
      setFilter,
    ],
  );
  _setFilterRef.current = _setFilter;

  const itemValue = values?.[props.name];
  useDebounce(
    () => {
      _setFilter(itemValue);
      if (props.onApply)
        props.onApply({
          title: props.title,
          name: props.name,
          value: itemValue,
        });
    },
    DELAY_WAITING_FOR_FILTER,
    [itemValue],
  );

  // Radix unmounts a closed panel's children, so a row's field only mounts
  // once its section is expanded. That matters: every sidebar row mounting
  // at once used to fire each AsyncSelectFilter's forced `autoFocus: true`
  // (useSelect.ts's `tableFilterProps`) simultaneously, starving the main
  // thread for over a second — reported live as the mobile filter drawer's
  // rows rendering empty and disappearing. react-bootstrap's Accordion.Body
  // mounted everything, so this used to need a manual `isExpanded` gate.
  return (
    <AccordionItem value={props.name}>
      <AccordionTrigger>{props.title}</AccordionTrigger>
      <AccordionContent>
        <div
          data-slot="filter-field"
          className={classNames(props.showValueBadge && 'mb-1.5')}
        >
          {props.children}
        </div>
        {props.showValueBadge && (
          <TableSidebarFilterValues
            value={itemValue}
            getValueLabel={getValueLabel}
            badgeValue={props.badgeValue}
            remove={removeValue}
            hideRemoveButton={props.hideRemoveButton}
          />
        )}
      </AccordionContent>
    </AccordionItem>
  );
};

const TableMenuFilterItem: FC<PropsWithChildren<TableFilterItemProps>> = ({
  getValueLabel = (value) => {
    if (value) {
      if (Array.isArray(value)) {
        return value.length;
      } else {
        return value?.label || value;
      }
    } else return value;
  },
  instantApply = true,
  ...props
}) => {
  const {
    table,
    setFilter,
    changeFilterValue,
    apply,
    selectedSavedFilter,
    registerFilterComponent,
    openMenuName,
    menuIsOpen,
    closeMenu,
    activeItemName,
    setActiveItemName,
  } = React.useContext(TableFilterContext);
  const values = useSelector(selectFilterValues(table));

  const _setFilterRef = useRef<any>();

  const removeValue = useCallback(
    (prevValue, value) => {
      let newValue;
      if (Array.isArray(prevValue) && prevValue.length > 1) {
        newValue = prevValue.filter((v) => !isEqual(v, value));
      } else {
        newValue = null;
      }
      apply(false);
      if (changeFilterValue) {
        changeFilterValue(props.name, newValue);
      }
      if (_setFilterRef.current) {
        _setFilterRef.current(newValue);
      }
      apply(true);
    },
    [changeFilterValue, props.name, apply],
  );

  const _setFilter = useCallback(
    (value) => {
      setFilter({
        label: props.title,
        name: props.name,
        value: value,
        component: () => (
          <TableSidebarFilterValues
            value={value}
            getValueLabel={getValueLabel}
            badgeValue={props.badgeValue}
            remove={removeValue}
            hideRemoveButton={props.hideRemoveButton}
          />
        ),
      });
    },
    [
      props.title,
      props.name,
      getValueLabel,
      props.badgeValue,
      removeValue,
      props.hideRemoveButton,
      setFilter,
    ],
  );
  _setFilterRef.current = _setFilter;

  // Register the filter renderer to access it from outside (from table cells)
  useEffect(() => {
    registerFilterComponent({
      name: props.name,
      setFilter: _setFilter,
    });
  }, [props.name, _setFilter]);

  const itemValue = values?.[props.name];

  // The filter field must have an initial value (at least null) so that the filter menu popup does not close when setting this filter for the first time.
  // Wait a moment for the filters to set to the form. Then use them in the table state.
  // Include itemValue in dependencies so filters loaded from URL are reflected in filtersStorage.
  useDebounce(
    () => {
      _setFilter(itemValue);
      if (itemValue === null && changeFilterValue) {
        changeFilterValue(props.name, null);
      }
    },
    DELAY_WAITING_FOR_FILTER,
    [itemValue],
  );

  // Update filter when selecting a saved filter
  useEffect(() => {
    if (!selectedSavedFilter?.values) return;
    const value = selectedSavedFilter.values[props.name];
    if (value) {
      _setFilter(value);
    } else {
      removeValue(value, value);
    }
  }, [selectedSavedFilter]);

  const onApply = (hideMenu = true) => {
    _setFilter(itemValue);
    if (props.onApply)
      props.onApply({ title: props.title, name: props.name, value: itemValue });
    apply(hideMenu);
  };

  // Fully independent fallback for standalone usage (no TableFiltersMenu
  // parent providing `setActiveItemName` — e.g. this component rendered
  // in isolation in a test), where there are no siblings to coordinate
  // with.
  const [localOpen, setLocalOpen] = useState(false);
  // Sibling-coordinated when a TableFiltersMenu "Add filter" list parent
  // is present: only one row across the whole list can be `open` at a
  // time, so opening this one implicitly closes any previously open
  // sibling — see FilterContextProvider.tsx's own comment on
  // `activeItemName` for why this exists (Radix's own default
  // outside-click dismissal alone needs a second click to actually open
  // a new row once another is already open).
  const open = setActiveItemName ? activeItemName === props.name : localOpen;
  const setOpen = useCallback(
    (next: boolean) => {
      if (setActiveItemName) {
        // The `next === false` (dismiss) branch only clears the shared
        // name if *this* row is still the one recorded active — guards
        // against a stale dismiss-outside callback (fired because a
        // sibling's trigger click looks like an "outside click" to
        // Radix) racing with that sibling's own open call and
        // clobbering it back to undefined.
        setActiveItemName((prev) =>
          next ? props.name : prev === props.name ? undefined : prev,
        );
      } else {
        setLocalOpen(next);
      }
    },
    [setActiveItemName, props.name],
  );

  // The column-header funnel icon (TableFiltersMenu.tsx's `openName`
  // branch) targets exactly one filter — its whole point is "this
  // column's own control," not the full filter list — so that one row
  // renders its field directly below, with no collapsed row/nested
  // Popover of its own, and every *other* row renders nothing at all
  // rather than showing a redundant full list alongside it (reported
  // live: the column icon opened the entire "Add filter"-style list,
  // with the target's own flyout then overlapping it — "dropdown menu is
  // not needed in this case").
  const isColumnMode = Boolean(openMenuName);
  const isColumnTarget = isColumnMode && openMenuName === props.name;

  // `skipFirstRun` guards against the column-target row: unlike the "Add
  // filter" list's own row (starts `open === false`, inert until a real
  // click), a column-target row is "open" from its very first render —
  // without this guard, instantApply's onApply() (a real
  // applyFiltersFn()/setFilter() dispatch) would fire during the initial
  // mount of *every* filterable column at once. That flood of
  // simultaneous cross-component dispatches, mid-mount for sibling
  // columns, reproduced a live "Should not already be working" React
  // invariant violation in Storybook — not caught by the jsdom suite,
  // which never mounts more than one filterable column at once. Real,
  // later value changes are unaffected.
  const skipFirstRun = useRef(true);
  useEffect(() => {
    if (skipFirstRun.current) {
      skipFirstRun.current = false;
      return;
    }
    if ((open || (isColumnTarget && menuIsOpen)) && instantApply) {
      // Don't hide menu when value changes (e.g., during typing)
      onApply(false);
    }
  }, [itemValue]);

  if (isColumnMode && !isColumnTarget) {
    return null;
  }

  if (isColumnTarget) {
    return (
      <div id={`filter-item-${props.name}`}>
        {/* `role="presentation"`, not `aria-hidden`: aria-hidden would
            hide the filter field inside from screen readers too. */}
        <div
          data-slot="filter-field"
          className="px-[12px] py-[8px]"
          onClick={(e) => e.stopPropagation()}
          role="presentation"
        >
          {/* Deferred until the popup itself is open, not mounted the
              moment this force-mounted row exists — see menuIsOpen's own
              comment in FilterContextProvider.tsx: mounting react-select
              (or similar) immediately, for every filterable column at
              once on page load, let its own auto-focus-on-mount behavior
              fire simultaneously across all of them. */}
          {menuIsOpen && props.children}
        </div>
        {!instantApply && menuIsOpen && (
          <>
            <div className="border-b border-b-secondary" />
            <div className="px-[12px] pt-[8px]">
              <div className="flex gap-3">
                <BaseButton
                  variant="tertiary"
                  className="grow w-1/2"
                  onClick={closeMenu}
                  label={translate('Cancel')}
                  size="lg"
                />
                <BaseButton
                  className="grow w-1/2"
                  onClick={() => onApply()}
                  label={translate('Apply')}
                  variant="primary"
                  size="lg"
                />
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // The "Add filter" list (openMenuName undefined): unchanged
  // accordion-row shape — click to expand this one row's own nested
  // Popover, coexisting with every other row in the same list.
  return (
    <div id={`filter-item-${props.name}`}>
      <MenuPopover open={open} onOpenChange={setOpen} modal={false}>
        <MenuPopover.Trigger asChild>
          {/* A real <button> so Tab reaches the row and Enter/Space open it. */}
          <MenuPopover.Item>
            {props.title}
            <CaretRightIcon size={20} className="ms-auto" weight="bold" />
          </MenuPopover.Item>
        </MenuPopover.Trigger>
        <MenuPopover.Content
          side="right"
          align="start"
          className="w-[375px] py-2.5"
          // Radix returns focus to this row on close, so the keyboard
          // stays in the list. Two exceptions skip that:
          // - a sibling row has taken over (`activeItemName` is set): the
          //   sibling would read the delayed focus return as an outside
          //   interaction and dismiss itself (see `activeItemName` in
          //   FilterContextProvider.tsx);
          // - the whole menu has closed: this row is hidden with it, and
          //   TableFiltersMenu moves focus to its trigger instead.
          onCloseAutoFocus={(e) =>
            (activeItemName || !menuIsOpen) && e.preventDefault()
          }
          onEscapeKeyDown={() => closeMenu?.()}
          onKeyDown={(e) => closeFlyoutOnTabOut(e, () => setOpen(false))}
        >
          {/* `role="presentation"` — see the column branch above. */}
          <div
            data-slot="filter-field"
            className="px-[12px] py-[8px]"
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            {open && props.children}
          </div>
          {!instantApply && (
            <>
              <div className="border-b border-b-secondary" />
              <div>
                {open && (
                  <div className="px-[12px] pt-[8px]">
                    <div className="flex gap-3">
                      <BaseButton
                        variant="tertiary"
                        className="grow w-1/2"
                        onClick={() => setOpen(false)}
                        label={translate('Cancel')}
                        size="lg"
                      />
                      <BaseButton
                        className="grow w-1/2"
                        onClick={() => onApply()}
                        label={translate('Apply')}
                        variant="primary"
                        size="lg"
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </MenuPopover.Content>
      </MenuPopover>
    </div>
  );
};

/** Please put only one child in each table filter item.
 *
 * `filterPosition` is only ever `'menu'` (`TableFiltersMenu`) or
 * `'sidebar'` (`TableFilterContainer`, the mobile drawer) by the time a
 * `TableFilterItem` actually renders — those are the only two components
 * that ever mount `props.filters` as real children. A third `'header'`
 * branch (`<Table filterPosition="header">`'s always-visible inline row)
 * used to live here too, but no real caller ever paired `filterPosition=
 * "header"` with `filters`, so it was unreachable and has been removed. */
export const TableFilterItem: FC<PropsWithChildren<TableFilterItemProps>> = ({
  ellipsis = true,
  ...props
}) => {
  const { filterPosition } = React.useContext(TableFilterContext);
  if (filterPosition === 'menu') {
    return <TableMenuFilterItem ellipsis={ellipsis} {...props} />;
  }
  return <TableSidebarFilterItem ellipsis={ellipsis} {...props} />;
};
